import { useEffect, useState } from 'react'
import { api, changesOf, shortDate, type AuditEvent } from '../../lib/api'
import { auditLabel } from '../../lib/auditLabels'
import { personLabel } from '../../lib/people'
import { ActivityFeed, ScrollPanel, type Tone } from '../kit'

// The latest acts against this vendor, newest first, one line each: the
// Overview's "Recent activity" (release 3.3, the 7 Oct plan section 3.3). Five
// show, the rest of the ten read scroll inside the panel (rule 2.1), and All
// history opens the History tab.
//
// It reads the same evidence chain the full History does, the last ten acts
// recorded against the vendor record, and says each in words. The chain holds
// the exact action code and payload; a reader scanning a profile wants "Tier
// moved to Tier 1 Critical", and the full trail is one link away for anybody
// who needs the hash. Only shown to roles that hold audit.read, because the
// route refuses everybody else.

// The words for each act are the one map every screen reads (lib/auditLabels.ts),
// so the activity panel and the Audit trail cannot describe one act two ways.

const TONE: Record<string, Tone> = {
  'vendor.approved': 'ok',
  'vendor.onboarded': 'ok',
  'vendor.exit_proposed': 'brand',
  'vendor.offboarded': 'plain',
  'vendor.tier_changed': 'info',
  'vendor.legal_hold_placed': 'special',
  'vendor.exit_stage_declined': 'crit',
  'vendor.incident_recorded': 'crit',
}

function describe(e: AuditEvent): string {
  if (e.action === 'vendor.tier_changed') {
    const to = (e.payload as { toLabel?: unknown } | null)?.toLabel
    return typeof to === 'string' && to ? 'Tier moved to ' + to : 'Tier moved'
  }
  const known = auditLabel(e.action)
  if (e.action === 'vendor.edited' || e.action === 'vendor.updated') {
    const fields = changesOf(e).map((c) => c.field)
    if (fields.length === 1) return known + ': ' + fields[0]
    if (fields.length > 1) return known + ': ' + fields.length + ' fields'
  }
  return known
}

// About five acts: an act is a line of words and a line for its date, 48 px
// with the gap between acts.
const ACTIVITY_HEIGHT = '15rem'

export function ActivityPanel({ vendorId, names, onOpenHistory }: { vendorId: string; names: Record<string, string>; onOpenHistory: () => void }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    setEvents(null)
    setFailed(false)
    api
      .audit({ entityType: 'vendor', entityId: vendorId, limit: 10 })
      .then((page) => {
        if (live) setEvents(page.events)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  return (
    <aside className="vd-panel vd-activity" aria-labelledby="vd-activity-h">
      <div className="vd-panel-head">
        <h2 id="vd-activity-h">Recent activity</h2>
        <button type="button" className="linkish" onClick={onOpenHistory}>
          All history
        </button>
      </div>
      {failed ? (
        <p className="vd-quiet">Could not read the history</p>
      ) : events === null ? (
        <p className="vd-quiet">Reading</p>
      ) : events.length === 0 ? (
        <p className="vd-quiet">Nothing recorded yet</p>
      ) : (
        <ScrollPanel label="Recent activity" maxHeight={ACTIVITY_HEIGHT}>
          <ActivityFeed
            items={events.map((e) => {
              const who = personLabel(names, e.actorId)
              return {
                key: e.eventId,
                text: (
                  <>
                    {describe(e)}
                    {who && <span className="vd-who"> by {who}</span>}
                  </>
                ),
                when: shortDate(e.occurredAt),
                tone: TONE[e.action],
              }
            })}
          />
        </ScrollPanel>
      )}
    </aside>
  )
}
